import { useEffect, useState } from 'react';
import {
  Autocomplete,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../reusable/CustomButton';
import Panel from '../reusable/Panel';
import AddIcon from '../assets/eoa-configuration-add.svg';
import useUserDetails from '../hooks/useUserDetails';
import { getOewsAreaTitles } from '../../services/eoaConfigServices';
import { useParams } from 'react-router-dom';
import { decimalNumberError, percentageError } from './fieldValidation';
import {
  REGIONAL_AREA_OPTIONS,
  SchoolInfoForm,
  SchoolInfoRow,
  YesNoAnswer,
  addSchool,
  confirmSchoolName,
  removeSchool,
  schoolNameError,
  showsDetailedCte,
  showsWageThresholds,
  updateSchool,
} from './schoolInfoForm';

interface SchoolInfoFieldsProps {
  value: SchoolInfoForm;
  onChange: (value: SchoolInfoForm) => void;
}

const placeholder = 'Your text here';

const YesNoField = ({
  name,
  label,
  value,
  onChange,
}: {
  name: string;
  label: string;
  value: YesNoAnswer;
  onChange: (value: YesNoAnswer) => void;
}) => (
  <fieldset className="eoa-configuration-field eoa-configuration-dsa">
    <legend className="eoa-configuration-field-label">{label}</legend>
    <RadioGroup name={name} value={value} onChange={(event) => onChange(event.target.value as YesNoAnswer)}>
      <FormControlLabel value="no" control={<Radio />} label="No" />
      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
    </RadioGroup>
  </fieldset>
);

const SchoolNameEditor = ({
  school,
  onChange,
  onApprove,
}: {
  school: SchoolInfoRow;
  onChange: (patch: Partial<SchoolInfoRow>) => void;
  onApprove: () => boolean;
}) => {
  const [showRequiredError, setShowRequiredError] = useState(false);
  const requiredError = showRequiredError ? schoolNameError(school.name) : null;

  return (
    <div className="eoa-configuration-field">
      <span className="eoa-configuration-field-label">School</span>
      <TextField
        fullWidth
        required
        autoFocus
        placeholder={placeholder}
        value={school.name}
        error={Boolean(requiredError)}
        helperText={requiredError}
        onChange={(event) => {
          setShowRequiredError(false);
          onChange({ name: event.target.value });
        }}
        onBlur={() => {
          if (!onApprove()) setShowRequiredError(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          if (!onApprove()) setShowRequiredError(true);
        }}
      />
    </div>
  );
};

const SchoolCard = ({
  school,
  expanded,
  editingName,
  areaTitles,
  onToggle,
  onEditName,
  onApproveName,
  onChange,
  onRemove,
}: {
  school: SchoolInfoRow;
  expanded: boolean;
  editingName: boolean;
  areaTitles: string[];
  onToggle: () => void;
  onEditName: () => void;
  onApproveName: () => boolean;
  onChange: (patch: Partial<SchoolInfoRow>) => void;
  onRemove: () => void;
}) => {
  const confirmed = school.nameConfirmed;
  const highWageHelperText = showsWageThresholds(school) ? decimalNumberError(school.highWageThreshold) : null;
  const highGrowthHelperText = showsWageThresholds(school) ? percentageError(school.highGrowthThreshold) : null;
  return (
    <Panel className="eoa-configuration-card eoa-configuration-school-card">
      {confirmed && !editingName ? (
        <div className="eoa-configuration-school-card-header">
          <button
            type="button"
            className="eoa-configuration-card-title eoa-configuration-school-name-button"
            onClick={onEditName}
          >
            {school.name.trim()}
            <EditOutlinedIcon className="eoa-configuration-school-name-edit-icon" aria-hidden="true" />
          </button>
          <IconButton
            className="eoa-configuration-school-toggle"
            aria-label={expanded ? 'Collapse school' : 'Expand school'}
            onClick={onToggle}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </div>
      ) : (
        <div className="eoa-configuration-school-card-header">
          <SchoolNameEditor school={school} onChange={onChange} onApprove={onApproveName} />
          {!confirmed && (
            <IconButton
              className="eoa-configuration-school-name-remove"
              aria-label="Remove unnamed school"
              onClick={onRemove}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      )}

      {confirmed && expanded && (
        <div className="eoa-configuration-fields">
          <YesNoField
            name={`primary-${school.id}`}
            label="Is this a primary school on the EOAA?"
            value={school.isPrimary}
            onChange={(isPrimary) => onChange({ isPrimary })}
          />

          <>
            <div className="eoa-configuration-inline-row eoa-configuration-school-details-row">
              <div className="eoa-configuration-field eoa-configuration-field--term-count">
                <span className="eoa-configuration-field-label">School number</span>
                <TextField
                  placeholder={placeholder}
                  value={school.schoolNumber}
                  onChange={(event) => onChange({ schoolNumber: event.target.value })}
                />
              </div>
              <div className="eoa-configuration-field">
                <span className="eoa-configuration-field-label">School type (e.g. comprehensive, magnet, etc)</span>
                <TextField
                  className="eoa-configuration-period-name-input"
                  placeholder={placeholder}
                  value={school.schoolType}
                  onChange={(event) => onChange({ schoolType: event.target.value })}
                />
              </div>
            </div>

            <YesNoField
              name={`graduation-${school.id}`}
              label="Can you provide graduation status for students?"
              value={school.graduationStatus}
              onChange={(graduationStatus) => onChange({ graduationStatus })}
            />
            <YesNoField
              name={`sat-${school.id}`}
              label="Does this school require students to take the SAT?"
              value={school.requiresSat}
              onChange={(requiresSat) => onChange({ requiresSat })}
            />
            <YesNoField
              name={`act-${school.id}`}
              label="Does this school require students to take the ACT?"
              value={school.requiresAct}
              onChange={(requiresAct) => onChange({ requiresAct })}
            />
            <YesNoField
              name={`cte-${school.id}`}
              label="Does this school have CTE courses?"
              value={school.hasCteCourses}
              onChange={(hasCteCourses) => onChange({ hasCteCourses })}
            />
            {showsDetailedCte(school) && (
              <YesNoField
                name={`cte-clusters-${school.id}`}
                label="Does this school have detailed CTE clusters?"
                value={school.hasDetailedCteClusters}
                onChange={(hasDetailedCteClusters) => onChange({ hasDetailedCteClusters })}
              />
            )}
            {showsWageThresholds(school) && (
              <div className="eoa-configuration-inline-row">
                <div className="eoa-configuration-field eoa-configuration-field--term-count">
                  <span className="eoa-configuration-field-label">High wage threshold</span>
                  <TextField
                    placeholder={'Number'}
                    value={school.highWageThreshold}
                    error={Boolean(highWageHelperText)}
                    helperText={highWageHelperText}
                    onChange={(event) => onChange({ highWageThreshold: event.target.value })}
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </div>
                <div className="eoa-configuration-field eoa-configuration-field--term-count">
                  <span className="eoa-configuration-field-label">High growth threshold</span>
                  <TextField
                    placeholder="Percentage"
                    value={school.highGrowthThreshold}
                    error={Boolean(highGrowthHelperText)}
                    helperText={highGrowthHelperText}
                    onChange={(event) => onChange({ highGrowthThreshold: event.target.value })}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </div>
              </div>
            )}

            <div className="eoa-configuration-inline-row">
              <div className="eoa-configuration-field eoa-configuration-field--term-count">
                <span className="eoa-configuration-field-label">Regional area</span>
                <Select
                  className="eoa-configuration-alias-period-select"
                  variant="standard"
                  displayEmpty
                  value={school.regionalAreaType}
                  onChange={(event) => onChange({ regionalAreaType: event.target.value })}
                  renderValue={(selected) =>
                    REGIONAL_AREA_OPTIONS.find((option) => option.value === selected)?.label || 'Area'
                  }
                >
                  {REGIONAL_AREA_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="eoa-configuration-field eoa-configuration-field--state">
                <span className="eoa-configuration-field-label">Regional area title</span>
                <Autocomplete
                  options={areaTitles}
                  value={school.regionalAreaTitle || null}
                  onChange={(_event, next) => onChange({ regionalAreaTitle: next ?? '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search for state"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="eoa-configuration-school-remove">
              <CustomButton
                variant="secondary"
                size="medium"
                buttonText="Remove this school"
                icon={<DeleteOutlineOutlinedIcon />}
                iconPosition="start"
                buttonAction={onRemove}
              />
            </div>
          </>
        </div>
      )}
    </Panel>
  );
};

const SchoolInfoFields = ({ value, onChange }: SchoolInfoFieldsProps) => {
  const { eoaId } = useParams<{ eoaId?: string }>();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const [areaTitles, setAreaTitles] = useState<string[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>(() =>
    value.schools.filter((school) => school.nameConfirmed).map((school) => school.id)
  );
  const [editingNameIds, setEditingNameIds] = useState<string[]>([]);

  useEffect(() => {
    const loadAreaTitles = async () => {
      const currentEoaId = eoaId ?? userDetails?.eoaId;
      if (!currentEoaId || !userDetails?.access_token) return;
      try {
        const response = await getOewsAreaTitles(currentEoaId, userDetails.access_token);
        setAreaTitles(response.data.area_titles);
      } catch {
        setAreaTitles([]);
      }
    };

    void loadAreaTitles();
  }, [eoaId, userDetails?.access_token, userDetails?.eoaId]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  return (
    <>
      <Panel className="eoa-configuration-card eoa-configuration-add-schools-header">
        <h3 className="eoa-configuration-card-title">Add schools</h3>
        <CustomButton
          className="eoa-configuration-add-period-button"
          variant="secondary"
          size="medium"
          buttonText="Add new school"
          icon={<img src={AddIcon} alt="" width={18} height={18} />}
          iconPosition="start"
          buttonAction={() => onChange(addSchool(value))}
        />
      </Panel>

      {value.schools.map((school) => (
        <SchoolCard
          key={school.id}
          school={school}
          expanded={expandedIds.includes(school.id)}
          editingName={editingNameIds.includes(school.id)}
          areaTitles={areaTitles}
          onToggle={() => toggleExpanded(school.id)}
          onEditName={() =>
            setEditingNameIds((current) => (current.includes(school.id) ? current : [...current, school.id]))
          }
          onApproveName={() => {
            if (schoolNameError(school.name) !== null) return false;
            const next = confirmSchoolName(value, school.id);
            const confirmed = Boolean(next.schools.find((row) => row.id === school.id)?.nameConfirmed);
            if (!confirmed) return false;
            setEditingNameIds((current) => current.filter((id) => id !== school.id));
            setExpandedIds((current) => (current.includes(school.id) ? current : [...current, school.id]));
            onChange(next);
            return true;
          }}
          onChange={(patch) => onChange(updateSchool(value, school.id, patch))}
          onRemove={() => {
            setExpandedIds((current) => current.filter((id) => id !== school.id));
            setEditingNameIds((current) => current.filter((id) => id !== school.id));
            onChange(removeSchool(value, school.id));
          }}
        />
      ))}
    </>
  );
};

export default SchoolInfoFields;
